import i18n from '@/i18n';
import shiftActionsController, {
  ShiftActionControlEnum,
} from '@/lib/actions/ShiftActionsController';
import { Icon } from '@/lib/enum/Icon';
import { userCan } from '@/lib/permission/userCan';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import { realtimeQuery } from '@/lib/realtime/weak/realtimeFunctions';
import { EmitCreateShiftData } from '@/lib/schedule-v2/scheduleV2Actions';
import { shiftHasConflict } from '@/lib/schedule-v2/shiftV2Functions';
import store from '@/store';
import { MenuItemType } from '@/types/propTypes';
import { isFuture, startOfNextDay } from '@/util/dateArithmetic';
import { computed, ComputedRef } from 'vue';
import {
  Employee,
  OpenShiftResponseStatusEnum,
  Shift,
  ShiftSwapStatusEnum,
} from '../../../api/v1';

export type ShiftActionType = MenuItemType & {
  useAsMain: boolean;
  showMainIcon?: boolean;
  mainButtonColour?: string;
};

export type ShiftActionExcludeOptions = Array<
  ShiftActionControlEnum | 'addShift'
>;

export const useShiftActions = (
  computedProps: ComputedRef<{
    useActions: boolean;
    shift: Shift;
    employee: Employee | null;
    employeeActions: boolean;
    excludedActions: ShiftActionExcludeOptions;
    emit: {
      (e: 'input', v?: boolean): void;
      (e: 'createShift', value: EmitCreateShiftData): void;
    };
  }>,
): ComputedRef<Array<ShiftActionType>> => {
  const openShiftResponseQuery = computed(() =>
    realtimeQuery(Entity.OpenShiftResponse)
      .where('shiftId', 'eq', computedProps.value.shift.id)
      .whereIn('status', [
        OpenShiftResponseStatusEnum.Pending,
        OpenShiftResponseStatusEnum.Accepted,
        OpenShiftResponseStatusEnum.Unavailable,
      ])
      .fetch(),
  );

  const shiftSwapQuery = computed(() =>
    realtimeQuery(Entity.ShiftSwap)
      .where('shiftId', 'eq', computedProps.value.shift.id)
      .whereIn('status', [
        ShiftSwapStatusEnum.PendingAcceptance,
        ShiftSwapStatusEnum.PendingApproval,
      ])
      .fetch(),
  );

  return computed<Array<ShiftActionType>>(() => {
    const {
      useActions,
      employeeActions,
      shift,
      employee,
      emit,
      excludedActions,
    } = computedProps.value;
    const { timezone, loggedInEmployee } = store.getters;
    const { scheduleSettings } = store.state.settings;

    if (!useActions) {
      return [];
    }

    // Use strings for ids, so it's easier to add additional actions
    const result: Array<ShiftActionType> =
      userCan.manageShifts() && !employeeActions
        ? [
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.ShiftHistory) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ShiftHistory
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.ShiftHistory,
              value: i18n.tc('schedule.dialog.shiftHistory'),
              icon: Icon.ClockRotateLeft,
              useAsMain: false,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ShiftHistory
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.ViewOpenShiftResponses,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ViewOpenShiftResponses
                ].isEnabled({
                  shift,
                  openShiftResponse:
                    openShiftResponseQuery.value.data[0] ?? null,
                }),
              id: ShiftActionControlEnum.ViewOpenShiftResponses,
              value: i18n.tc('button.viewOpenShiftResponses'),
              icon: Icon.UserCheck,
              useAsMain: !!openShiftResponseQuery.value.data.filter(
                (r) => r.status === OpenShiftResponseStatusEnum.Pending,
              ).length,
              showMainIcon: false,
              mainButtonColour: 'pink',
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ViewOpenShiftResponses
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.ReviewSwapShift,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ReviewSwapShift
                ].isEnabled({
                  shiftSwap: shiftSwapQuery.value.data[0] ?? null,
                }),
              id: ShiftActionControlEnum.ReviewSwapShift,
              value: i18n.tc('button.reviewShiftSwap'),
              icon: Icon.Shuffle,
              useAsMain: true,
              showMainIcon: false,
              mainButtonColour: 'pink',
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ReviewSwapShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide: excludedActions.includes(ShiftActionControlEnum.EditShift),
              id: ShiftActionControlEnum.EditShift,
              value: i18n.tc('button.editShift'),
              icon: Icon.CalendarPen,
              useAsMain:
                shiftHasConflict(shift) ||
                (!shift.lockedAt && isFuture(shift.startsAt)),
              showMainIcon: true,
              action: () => {
                shiftActionsController[ShiftActionControlEnum.EditShift].call(
                  shift,
                );
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.AssignShift) ||
                !shiftActionsController[
                  ShiftActionControlEnum.AssignShift
                ].isEnabled({
                  shift,
                }),
              id: ShiftActionControlEnum.AssignShift,
              value: i18n.tc('button.assignShift'),
              icon: Icon.UserPlus,
              useAsMain: false,
              action: () => {
                shiftActionsController[ShiftActionControlEnum.AssignShift].call(
                  shift,
                );
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.UnassignShift,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.UnassignShift
                ].isEnabled({
                  shift,
                }),
              id: ShiftActionControlEnum.UnassignShift,
              value: i18n.tc('button.unassignShift'),
              icon: Icon.CalendarXmark,
              useAsMain: false,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.UnassignShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.ReassignShift,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ReassignShift
                ].isEnabled({ shift, timezone }),
              id: ShiftActionControlEnum.ReassignShift,
              value: i18n.tc('button.reassignShift'),
              icon: Icon.CalendarDay,
              useAsMain: false,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ReassignShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.ReplaceShift) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ReplaceShift
                ].isEnabled({ shift, timezone }),
              id: ShiftActionControlEnum.ReplaceShift,
              value: i18n.tc('button.findReplacement'),
              icon: Icon.ArrowsRotate,
              useAsMain: true,
              showMainIcon: true,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ReplaceShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.MoveToUnassigned,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.MoveToUnassigned
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.MoveToUnassigned,
              value: i18n.tc('button.moveToUnassigned'),
              icon: Icon.UserPlus,
              useAsMain: false,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.MoveToUnassigned
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.MoveToOpen) ||
                !shiftActionsController[
                  ShiftActionControlEnum.MoveToOpen
                ].isEnabled({
                  shift,
                  scheduleSettings,
                }),
              id: ShiftActionControlEnum.MoveToOpen,
              value: i18n.tc('button.moveToOpenShifts'),
              icon: Icon.CircleDot,
              useAsMain: false,
              action: () => {
                shiftActionsController[ShiftActionControlEnum.MoveToOpen].call(
                  shift,
                );
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.DuplicateShift,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.DuplicateShift
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.DuplicateShift,
              value: i18n.tc('button.duplicateShift'),
              icon: Icon.Copy,
              useAsMain: false,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.DuplicateShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.PublishShift) ||
                !shiftActionsController[
                  ShiftActionControlEnum.PublishShift
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.PublishShift,
              value: i18n.tc('button.publishShift'),
              icon: `${Icon.CalendarCheck} text-green-600`,
              useAsMain: false,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.PublishShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.ViewTimesheet,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ViewTimesheet
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.ViewTimesheet,
              value: i18n.tc('button.viewTimesheet'),
              icon: Icon.ClipboardListCheck,
              useAsMain: true,
              showMainIcon: true,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ViewTimesheet
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.DeleteShift) ||
                !shiftActionsController[
                  ShiftActionControlEnum.DeleteShift
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.DeleteShift,
              value: i18n.tc('button.delete'),
              icon: `${Icon.TrashCan} text-red-600`,
              useAsMain: false,
              action: () => {
                shiftActionsController[ShiftActionControlEnum.DeleteShift].call(
                  shift,
                );
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes('addShift') ||
                !isFuture(startOfNextDay(shift.startsAt, timezone)),
              id: 'addShift',
              value: i18n.tc('button.addShift'),
              icon: Icon.Plus,
              dividerTop: true,
              useAsMain: false,
              action: () => {
                emit('createShift', {
                  employee,
                  startDate: shift.startsAt,
                  endDate: shift.startsAt,
                  isOpenShift: shift.open,
                });
                emit('input');
              },
            },
          ]
        : [
            {
              hide:
                excludedActions.includes(ShiftActionControlEnum.SwapShift) ||
                shiftSwapQuery.value.isLoading ||
                !shiftActionsController[
                  ShiftActionControlEnum.SwapShift
                ].isEnabled({
                  shift,
                  loggedInEmployeeId: loggedInEmployee.id,
                  scheduleSettings,
                  shiftSwaps: shiftSwapQuery.value.data,
                }),
              id: ShiftActionControlEnum.SwapShift,
              value: i18n.tc('schedule.dialog.swapShift'),
              icon: `${Icon.ArrowsRotate} text-green`,
              useAsMain: true,
              showMainIcon: true,
              action: () => {
                shiftActionsController[ShiftActionControlEnum.SwapShift].call(
                  shift,
                );
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.CancelShiftSwap,
                ) ||
                shiftSwapQuery.value.isLoading ||
                !shiftActionsController[
                  ShiftActionControlEnum.CancelShiftSwap
                ].isEnabled({
                  shift,
                  loggedInEmployeeId: loggedInEmployee.id,
                  scheduleSettings,
                  shiftSwaps: shiftSwapQuery.value.data,
                }),
              id: ShiftActionControlEnum.CancelShiftSwap,
              value: i18n.tc('schedule.dialog.cancelSwapShift'),
              icon: `${Icon.ArrowsRotate} text-red-600`,
              useAsMain: true,
              showMainIcon: true,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.CancelShiftSwap
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.ClaimOpenShift,
                ) ||
                openShiftResponseQuery.value.isLoading ||
                !shiftActionsController[
                  ShiftActionControlEnum.ClaimOpenShift
                ].isEnabled({
                  shift,
                  loggedInEmployeeId: loggedInEmployee.id,
                  openShiftResponses: openShiftResponseQuery.value.data,
                  scheduleSettings,
                }),
              id: ShiftActionControlEnum.ClaimOpenShift,
              value: shift.requiresApproval
                ? i18n.tc('button.requestOpenShift')
                : i18n.tc('button.claimOpenShift'),
              icon: Icon.CalendarCheck,
              useAsMain: true,
              showMainIcon: false,
              mainButtonColour: 'pink',
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ClaimOpenShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.WithdrawOpenShift,
                ) ||
                openShiftResponseQuery.value.isLoading ||
                !shiftActionsController[
                  ShiftActionControlEnum.WithdrawOpenShift
                ].isEnabled({
                  shift,
                  loggedInEmployeeId: loggedInEmployee.id,
                  openShiftResponses: openShiftResponseQuery.value.data,
                }),
              id: ShiftActionControlEnum.WithdrawOpenShift,
              value: i18n.tc('button.withdrawOpenShiftResponse'),
              icon: `${Icon.CalendarXmark} text-red`,
              useAsMain: true,
              showMainIcon: false,
              mainButtonColour: 'pink',
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.WithdrawOpenShift
                ].call(shift);
                emit('input');
              },
            },
            {
              hide:
                excludedActions.includes(
                  ShiftActionControlEnum.ViewTimesheet,
                ) ||
                !shiftActionsController[
                  ShiftActionControlEnum.ViewTimesheet
                ].isEnabled({ shift }),
              id: ShiftActionControlEnum.ViewTimesheet,
              value: i18n.tc('button.viewTimesheet'),
              icon: Icon.ClipboardListCheck,
              useAsMain: true,
              showMainIcon: true,
              action: () => {
                shiftActionsController[
                  ShiftActionControlEnum.ViewTimesheet
                ].call(shift);
                emit('input');
              },
            },
          ];

    return result.filter((item) => !item.hide);
  });
};
